import { Tile } from "@dropbox/dash-component-library";
import { Button } from "@dropbox/dig-components/buttons";
import { FileIcon } from "@dropbox/dig-content-icons";
import { Cluster } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  ChevronDownLine,
  ChevronUpLine,
} from "@dropbox/dig-icons/dist/mjs/assets";
import { useTheme } from "@emotion/react";
import { openURL } from "@mirage/service-platform-actions";
import i18n from "@mirage/translations";
import { isEmpty, last, take, uniqBy } from "lodash";
import { useState } from "react";
import styles from "./ResultAttachments.module.css";

import type { dash } from "@dropbox/api-v2-client";

type ResultAttachmentsProps = {
  attachments: dash.LinkAttachment[];
};
const MAX_ATTACHMENTS_TO_SHOW = 3;
export const ResultAttachments: React.FC<ResultAttachmentsProps> = ({
  attachments,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isDarkMode = useTheme();
  const uniqueAttachments: dash.LinkAttachment[] = uniqBy(
    attachments,
    (attachment) => `${attachment.title}-${attachment.url}`,
  );
  const shouldShowMore = uniqueAttachments.length > MAX_ATTACHMENTS_TO_SHOW;

  if (isEmpty(uniqueAttachments)) {
    return null;
  }

  return (
    <Cluster gap="8" className={styles.attachments}>
      {shouldShowMore && (
        <Button
          variant="borderless"
          withIconStart={
            <UIIcon src={expanded ? ChevronUpLine : ChevronDownLine} />
          }
          size="small"
          onClick={() => setExpanded(!expanded)}
        >
          {i18n.t(expanded ? "show_less_answer" : "show_more_answer")}
        </Button>
      )}
      {(expanded
        ? uniqueAttachments
        : take(uniqueAttachments, MAX_ATTACHMENTS_TO_SHOW)
      ).map((attachment) => {
        const extension = last(attachment.title?.split("."));
        const iconUrl = attachment.icon
          ? isDarkMode
            ? attachment.icon.dark_png_url
            : attachment.icon.light_png_url
          : "";
        const icon = iconUrl ? (
          <img
            className={styles.attachmentIcon}
            src={iconUrl}
            alt={attachment.title}
          />
        ) : extension ? (
          <FileIcon
            extension={extension}
            size="small"
            hasWhitespace={false}
            hasBackground={false}
          />
        ) : undefined;
        return (
          <Cluster.Item key={attachment.url}>
            <Tile
              className={styles.attachmentTile}
              size="small"
              onClick={() => openURL(attachment.url)}
              withLabel={attachment.title || ""}
              withLabelProps={{
                className: styles.attachmentLabel,
              }}
              withStartAccessory={icon}
            />
          </Cluster.Item>
        );
      })}
    </Cluster>
  );
};
